import * as React from 'react'
import { graphql } from 'gatsby'
import { CategoryPageHero } from 'components/Hero/CategoryPageHero'
import { SectionValuesList } from 'components/SectionValuesList/SectionValuesList'
import { AppBanner } from 'components/Banner/AppBanner'
import { SEO } from 'components/SEO/SEO'
import { SectionWithTitle } from 'components/SectionWithTitle/SectionWithTitle'
import { ContentfulCategoryPageQuery } from '../../graphql/generated-types'
import {
  mapContentfulStoreIconsToColorsSet,
  mapContentfulSubCategoriesToCategoriesTileData,
  mapContentfulValues,
} from 'utils/helpers'
import { CategoriesSection } from 'components/CategoriesSection/CategoriesSection'
import { SharedPageContext } from './types'
import { PromoDisclaimerSection } from 'components/PromoDisclaimerSection/PromoDisclaimerSection'

export type CategoryPageTemplateProps = {
  pageContext: SharedPageContext
  data: ContentfulCategoryPageQuery
}

const CategoryPageTemplate = ({
  pageContext: { sharedPageData },
  data,
}: CategoryPageTemplateProps) => {
  const { hero, copy, seo, subCategories } = data.contentfulCategory
  const {
    storesIcons,
    appBannerSection,
    valuesListSection,
    promotion,
    isPromoActive,
  } = sharedPageData

  const storesIconsMapped = mapContentfulStoreIconsToColorsSet(storesIcons)

  return (
    <>
      <SEO title={seo.title} description={seo.description.description} />
      <CategoryPageHero
        img={hero.image}
        backgroundColor={hero.color}
        title={hero.title}
      />
      <SectionWithTitle
        title={copy.title}
        textContent={copy.text}
        disclaimers={copy.disclaimers}
      />
      <CategoriesSection
        title={subCategories.title}
        categories={mapContentfulSubCategoriesToCategoriesTileData(
          subCategories.data
        )}
      />
      <SectionValuesList
        values={mapContentfulValues(valuesListSection.values)}
      />
      {isPromoActive && promotion ? (
        <PromoDisclaimerSection text={promotion.disclaimer} />
      ) : null}
      <AppBanner
        image={appBannerSection.image}
        storesIcons={storesIconsMapped.black}
        text={appBannerSection.desktop}
        mobileText={appBannerSection.mobile}
      />
    </>
  )
}

export const query = graphql`
  fragment ContentfulImageData on ContentfulMainImage {
    image {
      gatsbyImageData(placeholder: BLURRED)
    }
    alternativeText
  }

  query ContentfulCategoryPage($id: String!) {
    contentfulCategory(id: { eq: $id }) {
      name
      copy {
        title
        disclaimers
        text {
          raw
        }
      }
      hero {
        title
        color
        image {
          ...ContentfulImageData
        }
      }
      seo {
        title
        description {
          description
        }
      }
      subCategories {
        title
        data {
          id
          name
          description
          image {
            ...ContentfulImageData
          }
        }
      }
    }
  }
`

export default CategoryPageTemplate
